import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import utc from 'dayjs/plugin/utc'
import moment, { Moment } from 'moment'

import { NamedItemString } from 'core/domain/common/namedItem'

dayjs.extend(utc)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

export const formatDate = (date: Date | string) => {
  return dayjs.utc(date)
}

export const parseZone = (date: Date | Moment | string) => {
  return moment.parseZone(date)
}

export const toDateWithoutTimeZone = (date: Date | Moment | string) => {
  const dateAsString = moment.parseZone(date).format('YYYY-MM-DD')
  return moment(dateAsString)
}

export const toDateWithoutTimeZoneV2 = (date: string) => {
  return dayjs.utc(date)
}

export const now = () => {
  return moment()
}

export const nowV2 = () => {
  return toDateWithoutTimeZoneV2(nowAsString())
}

export const nowAsString = () => {
  return dayjs().format('YYYY-MM-DD')
}

export const isTodayAfter = (date: Date | string) => {
  return now().startOf('date').isAfter(parseZone(date))
}

export const currentYear = () => {
  return moment().year()
}

export const disabledBirthDate = (date) => {
  if (date && date > dayjs().subtract(16, 'year')) {
    return true
  }

  return false
}

export const subtract = (date: any, amount: number, unit: 'month' | 'day' | 'year' | 'week') => {
  return date.clone().subtract(amount, unit)
}

export const addDays = (date: any, days: number) => {
  return date.clone().add(days, 'days')
}

export const addDaysV2 = (date: any, days: number) => {
  const dateAsDayjs = dayjs.isDayjs(date) ? date : dayjs.utc(date)
  return dateAsDayjs.add(days, 'days')
}

export const formatYear = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('YYYY')
}

export const formatYearV2 = (date: any) => {
  const dateAsDayjs = dayjs.isDayjs(date) ? date : dayjs.utc(date)
  return dateAsDayjs.format('YYYY')
}

export const parseZoneFormat = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MM/DD/YYYY')
}

export const parseZoneFormatV2 = (date: string) => {
  return dayjs.utc(date).format('MM/DD/YYYY')
}

export const submitDateToServerFormat = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('YYYY-MM-DD')
}

export const submitDateToServerFormatV2 = (date: string | dayjs.Dayjs) => {
  return dayjs.utc(date).format('YYYY-MM-DD')
}

export const parseZoneMonthYearFormat = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MMMM YYYY')
}

export const parseZoneDayMonthYearFormat = (date: string | dayjs.Dayjs) => {
  return dayjs.utc(date).format('MMMM DD, YYYY')
}

export const parseZoneMonthYearDashFormat = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MM-YYYY')
}

export const formatMonthYear = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MMMM YYYY')
}

export const parseZoneFormatShortMonthYear = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MMM YYYY')
}

export const formatShortMonthYear = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MMM YYYY')
}

export const formatShortMonthYearV2 = (date: string | dayjs.Dayjs) => {
  return dayjs.utc(date).format('MMM YYYY')
}

export const formatDataAsDashed = (date: Date | Moment) => {
  return moment.parseZone(date).format('MM-DD-YYYY')
}

export const formatDataAsDashedV2 = (date: string | dayjs.Dayjs) => {
  return dayjs.utc(date).format('MM-DD-YYYY')
}

export const formatDataAsUnderscore = (date: string | dayjs.Dayjs) => {
  return dayjs.utc(date).format('MM_DD_YYYY')
}

export const formatDateWithUtc = (date: Date | Moment) => {
  return moment.parseZone(date).format('MM/DD/YYYY')
}

export const formatDateTime = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MM/DD/YYYY HH:mm:ss')
}

export const formatDateTimeAMPM = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MM/DD/YYYY HH:mm A')
}

export const formatDateOnlyTime = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('HH:mm:ss')
}
export const formatMonthAndDateOnly = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MMM DD') // Jun 30
}

export const formatLongMonthAndDateOnly = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MMMM DD') // Jun 30
}

export const formatMonth = (date: Date | Moment | string) => {
  return moment.parseZone(date).format('MMMM')
}

export const isFutureDate = (date: any): boolean => {
  const dateAsDayjs = dayjs.isDayjs(date) ? date : dayjs.utc(date)
  return dateAsDayjs > dayjs().endOf('day')
}

export const isPastDate = (date: any): boolean => {
  const dateAsDayjs = dayjs.isDayjs(date) ? date : dayjs.utc(date)
  return dateAsDayjs < dayjs().startOf('day')
}

export const isBefore = (date1: any, date2: any): boolean => {
  const date1AsDayjs = dayjs.isDayjs(date1) ? date1 : dayjs.utc(date1)
  const date2AsDayJs = dayjs.isDayjs(date2) ? date2 : dayjs.utc(date2)
  return date1AsDayjs.utc(date1).isBefore(date2AsDayJs)
}

export const isAfter = (date1: any, date2: any): boolean => {
  const date1AsDayjs = dayjs.isDayjs(date1) ? date1 : dayjs.utc(date1)
  const date2AsDayJs = dayjs.isDayjs(date2) ? date2 : dayjs.utc(date2)
  return date1AsDayjs.utc(date1).isAfter(date2AsDayJs)
}

export const getMonths = (startDate: string, endDate: string, displayFormat: string = null): NamedItemString[] => {
  const startMonth = dayjs.utc(startDate)
  const planPeriodEndMonth = dayjs.utc(endDate)
  const today = dayjs.utc()
  const endMonth = today.isBefore(planPeriodEndMonth) ? today : planPeriodEndMonth

  const resultMonths: NamedItemString[] = []
  let iteratorMonth = startMonth
  while (iteratorMonth.isBefore(endMonth)) {
    resultMonths.push({
      id: submitDateToServerFormatV2(iteratorMonth),
      name: displayFormat ? dayjs.utc(iteratorMonth).format(displayFormat) : formatShortMonthYearV2(iteratorMonth),
    })
    iteratorMonth = iteratorMonth.add(1, 'months')
  }

  return resultMonths
}

export const calculateDays = (startDate: any, endDate: any) => {
  const startDateAsDayjs = dayjs.isDayjs(startDate) ? startDate : dayjs.utc(startDate)
  startDateAsDayjs.set('hour', 0).set('minute', 0).set('second', 0)
  const endDateAsDayjs = dayjs.isDayjs(endDate) ? endDate : dayjs.utc(endDate)
  endDateAsDayjs.set('hour', 0).set('minute', 0).set('second', 0)
  return endDateAsDayjs.diff(startDateAsDayjs, 'days')
}
